import { useState, useEffect } from 'react';
import './MicrophoneHelper.css';

type MicError = 
  | 'permission-denied'
  | 'no-audio-detected'
  | 'silent-recording'
  | 'mic-not-found'
  | 'mic-in-use'
  | 'transcription-failed'
  | 'https-required'
  | 'not-supported'
  | 'generic-error'
  | null;

interface MicrophoneHelperProps {
  error: MicError;
  onRetry: () => void;
  onDismiss: () => void;
  isRecording?: boolean;
  audioLevel?: number; // 0-1
}

const errorContent: Record<NonNullable<MicError>, {
  emoji: string;
  title: string;
  description: string;
  tips: string[];
  buttonText: string;
}> = {
  'permission-denied': {
    emoji: '🔒',
    title: 'Microfone bloqueado',
    description: 'Seu navegador não deixou o Ataric usar o microfone.',
    tips: [
      'Recarregue a página',
      'Quando aparecer "Permitir microfone", clique em Permitir',
      'Se não aparecer, clique no cadeado 🔒 na barra de endereço'
    ],
    buttonText: 'Recarregar página'
  },
  'no-audio-detected': {
    emoji: '🎤',
    title: 'Não captei sua voz',
    description: 'Parece que você soltou o botão muito rápido.',
    tips: [
      'Segure o botão enquanto fala',
      'Solte só quando terminar de falar',
      'Fale perto do microfone'
    ],
    buttonText: 'Tentar de novo'
  },
  'silent-recording': {
    emoji: '🔇',
    title: 'Gravei só silêncio',
    description: 'O microfone está funcionando, mas não captou sua voz.',
    tips: [
      'Fale mais alto ou mais perto do microfone',
      'Verifique se não está no mudo',
      'Se usar fone de ouvido, fale perto do mic do fone'
    ],
    buttonText: 'Tentar de novo'
  },
  'mic-not-found': {
    emoji: '🎧',
    title: 'Microfone não encontrado',
    description: 'Não achei nenhum microfone no seu dispositivo.',
    tips: [
      'Conecte um fone de ouvido com microfone',
      'Ou use um microfone externo',
      'Verifique se o microfone está ligado'
    ],
    buttonText: 'Tentar de novo'
  },
  'mic-in-use': {
    emoji: '📱',
    title: 'Microfone ocupado',
    description: 'Outro app está usando o microfone agora.',
    tips: [
      'Feche outras abas do navegador',
      'Feche apps como WhatsApp, Zoom, Meet',
      'Depois volte aqui e tente de novo'
    ],
    buttonText: 'Tentar de novo'
  },
  'transcription-failed': {
    emoji: '🤔',
    title: 'Não entendi o áudio',
    description: 'Consegui gravar, mas não entendi o que você disse.',
    tips: [
      'Fale mais devagar e claro',
      'Evite barulhos de fundo',
      'Tente frases mais curtas'
    ],
    buttonText: 'Tentar de novo'
  },
  'https-required': {
    emoji: '🔐',
    title: 'Conexão segura necessária',
    description: 'O microfone só funciona em sites seguros (HTTPS).',
    tips: [
      'Acesse o site usando https:// no início do endereço',
      'Ou use o app em vez do navegador',
      'Se estiver em WiFi público, tente dados móveis'
    ],
    buttonText: 'Entendi'
  },
  'not-supported': {
    emoji: '📱',
    title: 'Navegador não suportado',
    description: 'Seu navegador não suporta gravação de áudio.',
    tips: [
      'Use o Safari (iPhone) ou Chrome (Android)',
      'Atualize seu navegador para a versão mais recente',
      'Tente acessar pelo app nativo'
    ],
    buttonText: 'Entendi'
  },
  'generic-error': {
    emoji: '😕',
    title: 'Algo deu errado',
    description: 'Tivemos um probleminha técnico.',
    tips: [
      'Tente de novo',
      'Se continuar, recarregue a página',
      'Ainda com problemas? Tente outro navegador'
    ],
    buttonText: 'Tentar de novo'
  }
};

export function MicrophoneHelper({ error, onRetry, onDismiss, isRecording, audioLevel = 0 }: MicrophoneHelperProps) {
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    if (error) {
      setShowModal(true);
    }
  }, [error]);

  const handleRetry = () => {
    if (error === 'permission-denied') {
      window.location.reload();
    } else {
      setShowModal(false);
      onRetry();
    }
  };

  const handleDismiss = () => {
    setShowModal(false);
    onDismiss();
  };

  // Only show error modal, audio level is now integrated into the button
  if (!error || !showModal) return null;

  const content = errorContent[error];

  return (
    <div className="mic-helper-overlay" onClick={handleDismiss}>
      <div className="mic-helper-modal" onClick={e => e.stopPropagation()}>
        <button className="mic-helper-close" onClick={handleDismiss}>×</button>
        
        <div className="mic-helper-emoji">{content.emoji}</div>
        <h2 className="mic-helper-title">{content.title}</h2>
        <p className="mic-helper-description">{content.description}</p>
        
        <div className="mic-helper-tips">
          <p className="mic-helper-tips-title">💡 O que fazer:</p>
          <ol className="mic-helper-tips-list">
            {content.tips.map((tip, i) => (
              <li key={i}>{tip}</li>
            ))}
          </ol>
        </div>

        <button className="mic-helper-button" onClick={handleRetry}>
          {content.buttonText}
        </button>
        
        <button className="mic-helper-dismiss" onClick={handleDismiss}>
          Voltar
        </button>
      </div>
    </div>
  );
}

// Hook to manage microphone errors
export function useMicrophoneHelper() {
  const [micError, setMicError] = useState<MicError>(null);
  const [audioLevel, setAudioLevel] = useState(0);

  const setError = (errorType: MicError) => {
    setMicError(errorType);
  };

  const clearError = () => {
    setMicError(null);
  };

  const handleRecorderError = (error: any): MicError => {
    const message = error?.message?.toLowerCase() || '';
    
    if (message.includes('permission') || message.includes('denied') || message.includes('not allowed')) {
      return 'permission-denied';
    }
    if (message.includes('not found') || message.includes('no device')) {
      return 'mic-not-found';
    }
    if (message.includes('in use') || message.includes('not readable')) {
      return 'mic-in-use';
    }
    return 'generic-error';
  };

  return {
    micError,
    setError,
    clearError,
    audioLevel,
    setAudioLevel,
    handleRecorderError
  };
}

export default MicrophoneHelper;
